// @owners { team: patients }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { get } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { staleTimes } from '~shared/queries/constants';
import { useSelectorShared } from '~shared/store';
import { type APIError } from '~shared/types';
import { type Insurance } from '~shared/types/clients';

type FetchAllInsurancesResponse = Insurance[] & { error?: APIError };

async function fetchInsuranceByID(insuranceID: number | null | undefined): Promise<Insurance> {
  const response = await get(`/insurances/${insuranceID}`);
  if (response.error) {
    throw new Error(getErrorMessageFromResponse({ error: response.error }));
  }
  return response as Insurance;
}

const EMPTY_INSURANCES: Insurance[] = [];

export const insurances = createQueryKeys('insurances', {
  fetchAll: () => ({
    queryKey: ['fetchAll'],
    queryFn: async () => {
      const response: FetchAllInsurancesResponse = await get('/insurances');
      const insuranceFetchError = response.error;
      if (insuranceFetchError) {
        throw new Error(getErrorMessageFromResponse({ error: insuranceFetchError }));
      }
      return response;
    },
  }),
  fetchOne: ({ insuranceID }: { insuranceID: number | null | undefined }) => ({
    queryKey: [{ insuranceID }],
    queryFn: () => fetchInsuranceByID(insuranceID),
  }),
});

const useInsuranceByIDFromCache = (insuranceID: number | undefined | null) => {
  const queryClient = useQueryClient();
  const cachedInsurances: Insurance[] = queryClient.getQueryData(insurances.fetchAll().queryKey) ?? [];
  return cachedInsurances.find((insurance) => insurance.id === insuranceID);
};

/**
 * Fetch insurance by id for a patient
 * @param insuranceID
 * @returns insurance
 */
export const useGetInsuranceByID = (insuranceID: number | undefined | null) => {
  const queryClient = useQueryClient();
  const cachedInsurance = useInsuranceByIDFromCache(insuranceID);
  const { data, isLoading, isSuccess, error } = useQuery({
    queryKey: insurances.fetchOne({ insuranceID }).queryKey,
    enabled: !cachedInsurance && !!insuranceID,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
    queryFn: async () => {
      const insurance = await fetchInsuranceByID(insuranceID);
      queryClient.setQueryData(insurances.fetchAll().queryKey, (prev: Insurance[] | undefined) => {
        if (!prev?.length) {
          return [insurance];
        }
        const without = prev.filter((i) => i.id !== insurance.id);
        return [...without, insurance];
      });
      return insurance;
    },
  });

  return {
    insurance: cachedInsurance ?? data,
    isLoading,
    error,
    isSuccess,
  };
};

/**
 * Fetch all insurances for a patient, including family member insurances.
 * Runs only when authorized; `options.enabled` is AND-ed with auth (defaults to true when omitted).
 * @returns Array of insurances
 */
export const useGetInsurances = (options?: { enabled?: boolean }) => {
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const enabled = isAuthorized && (options?.enabled ?? true);
  const { data, isLoading, isSuccess, error, refetch } = useQuery({
    ...insurances.fetchAll(),
    enabled,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  const fetchedInsurances = data ?? EMPTY_INSURANCES;

  return {
    insurances: fetchedInsurances,
    error,
    isLoading,
    isSuccess,
    hasActiveInsurances: fetchedInsurances.some((insurance) => insurance.is_active),
    hasInsurance: fetchedInsurances.length > 0,
    hasGovernmentInsurance: fetchedInsurances.some(({ is_government_insurance }) => !!is_government_insurance),
    refetch,
  };
};

/**
 * Fetch all insurances for a given patient
 * @param userID patient's user ID
 * @returns  Array of insurances
 */
export const useGetInsurancesByUserID = ({ userID }: { userID: number | null | undefined }) => {
  const getInsuranceByUserID = useCallback(
    (allInsurances: Insurance[]) => {
      return allInsurances.filter(({ user_id }) => user_id === userID);
    },
    [userID],
  );

  const { data, isLoading, isSuccess, error } = useQuery({
    ...insurances.fetchAll(),
    select: getInsuranceByUserID,
    enabled: !!userID,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  return {
    hasActiveInsurances: (data ?? []).some((insurance) => insurance.is_active),
    hasInsurances: !!data?.length,
    insurances: data ?? EMPTY_INSURANCES,
    isLoading,
    isSuccess,
    error,
  };
};

/**
 * Fetch all insurances found via cardfinder service
 * @returns Array of insurances
 */
export const useGetCardFinderInsurances = () => {
  const getCardFinderInsurances = useCallback((allInsurances: Insurance[]) => {
    return allInsurances.filter(({ source }) => source === 'cardfinder');
  }, []);

  const { data, isLoading, error } = useQuery({
    ...insurances.fetchAll(),
    select: getCardFinderInsurances,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });
  const cardFinderInsurances = data ?? EMPTY_INSURANCES;

  return {
    insurances: cardFinderInsurances,
    hasCardFinderInsurance: cardFinderInsurances.length > 0,
    isLoading,
    error,
  };
};
